import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { LookupStore } from '@core/lookup/lookup.store';
import { LOOKUP_CODES } from '@core/lookup/lookup-codes';
import { LookupResponse } from '@core/models/common.model';
import { PermissionService } from '@core/services/permission.service';
import { CompanyApiService } from '@features/company/company-api.service';
import { Permission } from '@shared/enums/permission.enum';
import { LookupSelectComponent } from '@shared/components/lookup-select/lookup-select.component';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { nullIfBlank } from '@shared/utils/payload-utils';
import { EligibilityDependantSectionComponent } from '../eligibility-dependant-section/eligibility-dependant-section.component';
import { EmployeeInterventionsPanelComponent } from '@features/employee/components/employee-interventions-panel/employee-interventions-panel.component';
import { InterventionStore } from '../../../intervention.store';
import { EligibilityMemberRequest } from '../../../intervention.model';

// The Eligibility step: who the member is (a snapshot the case keeps), the Momentum products, a dependant when the case is for one, and the
// decision (Member eligible: Yes or No). The clinician corrects the member's details here and the correction is saved on the employee, so the
// next case starts right. Once eligibility passes the snapshot is locked and this step is read only.
@Component({
  selector: 'intervention-eligibility-panel',
  imports: [DatePipe, ReactiveFormsModule, EligibilityDependantSectionComponent, EmployeeInterventionsPanelComponent, LookupSelectComponent, ...SharedMaterialUIModule],
  templateUrl: './intervention-eligibility-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InterventionEligibilityPanelComponent {
  protected readonly store = inject(InterventionStore);
  private readonly lookupStore = inject(LookupStore);
  private readonly permissionService = inject(PermissionService);
  private readonly companyApi = inject(CompanyApiService);
  private readonly formBuilder = inject(FormBuilder);

  readonly interventionId = input.required<string>();
  readonly interventionStatus = input<string | null>(null);

  protected readonly editingMember = signal(false);
  protected readonly chosenProducts = signal<string[]>([]);

  // The company's own lists (job titles and organisational levels 1 to 3), read for the employee's company.
  protected readonly jobTitles = signal<LookupResponse[]>([]);
  protected readonly levels = signal<{ level: number; item: LookupResponse }[]>([]);

  protected readonly genders = computed(() => this.lookupStore.items(LOOKUP_CODES.gender));
  protected readonly maritalStatuses = computed(() => this.lookupStore.items(LOOKUP_CODES.maritalStatus));
  protected readonly momentumProducts = computed(() => this.lookupStore.items(LOOKUP_CODES.momentumProduct));

  protected readonly data = computed(() => this.store.eligibility());
  protected readonly locked = computed(() => this.data()?.locked ?? false);

  protected readonly canEdit = computed(
    () => this.permissionService.hasPermission(Permission.InterventionsUpdate) && this.interventionStatus() !== 'Closed' && !this.locked(),
  );

  protected readonly memberForm = this.formBuilder.nonNullable.group({
    firstName: ['', [Validators.required, Validators.maxLength(100)]],
    surname: ['', [Validators.required, Validators.maxLength(100)]],
    idNumber: ['', Validators.maxLength(30)],
    dateOfBirth: [''],
    genderId: [''],
    maritalStatusId: [''],
    contactNumber: ['', Validators.maxLength(50)],
    emailAddress: ['', [Validators.email, Validators.maxLength(320)]],
    companyJobTitleId: [''],
    level1Id: [''],
    level2Id: [''],
    level3Id: [''],
  });

  protected readonly decisionForm = this.formBuilder.nonNullable.group({
    eligible: ['yes'],
    detail: ['', Validators.maxLength(1000)],
  });

  constructor() {
    this.lookupStore.load([LOOKUP_CODES.gender, LOOKUP_CODES.maritalStatus, LOOKUP_CODES.momentumProduct]);

    effect(() => this.store.loadEligibility(this.interventionId()));

    // The ticked products follow what is saved.
    effect(() => this.chosenProducts.set((this.data()?.products ?? []).map((x) => x.momentumProductId)));

    // The company lists are read when the employee's company is known.
    effect(() => {
      const companyId = this.data()?.companyId;
      if (!companyId) {
        return;
      }

      this.companyApi.getCompanyJobTitles({ companyId }).subscribe({
        next: (items) => this.jobTitles.set(items.map((x) => ({ id: x.id, name: x.name, code: null }))),
        error: () => undefined,
      });
      this.companyApi.getCompanyOrganisationalLevels({ companyId }).subscribe({
        next: (items) =>
          this.levels.set(items.filter((x) => x.level <= 3).map((x) => ({ level: x.level, item: { id: x.id, name: x.name, code: null } }))),
        error: () => undefined,
      });
    });
  }

  protected levelOptions(level: number): LookupResponse[] {
    return this.levels()
      .filter((x) => x.level === level)
      .map((x) => x.item);
  }

  protected editMember(): void {
    const member = this.data()?.member;
    this.memberForm.reset({
      firstName: member?.memberName ?? '',
      surname: member?.memberSurname ?? '',
      idNumber: member?.memberIdNumber ?? '',
      dateOfBirth: member?.memberDateOfBirth ?? '',
      genderId: member?.memberGenderId ?? '',
      maritalStatusId: member?.memberMaritalStatusId ?? '',
      contactNumber: member?.memberMobileNumber ?? '',
      emailAddress: member?.memberEmailAddress ?? '',
      companyJobTitleId: member?.companyJobTitleId ?? '',
      level1Id: member?.level1Id ?? '',
      level2Id: member?.level2Id ?? '',
      level3Id: member?.level3Id ?? '',
    });
    this.editingMember.set(true);
  }

  protected saveMember(): void {
    if (this.memberForm.invalid) {
      this.memberForm.markAllAsTouched();
      return;
    }

    const value = this.memberForm.getRawValue();
    const request: EligibilityMemberRequest = {
      firstName: nullIfBlank(value.firstName),
      surname: nullIfBlank(value.surname),
      idNumber: value.idNumber.trim(),
      dateOfBirth: nullIfBlank(value.dateOfBirth),
      genderId: nullIfBlank(value.genderId),
      maritalStatusId: nullIfBlank(value.maritalStatusId),
      contactNumber: value.contactNumber.trim(),
      emailAddress: value.emailAddress.trim(),
      companyJobTitleId: nullIfBlank(value.companyJobTitleId),
      level1Id: nullIfBlank(value.level1Id),
      level2Id: nullIfBlank(value.level2Id),
      level3Id: nullIfBlank(value.level3Id),
    };

    this.store.saveEligibilityMember(this.interventionId(), request, () => this.editingMember.set(false));
  }

  protected isProductChosen(id: string): boolean {
    return this.chosenProducts().includes(id);
  }

  // The products are shown as saved until the clinician changes a tick; "No Momentum product" excludes the others.
  protected toggleProduct(item: LookupResponse, checked: boolean): void {
    const exclusive = this.isExclusive(item);
    const current = this.chosenProducts();
    let next = checked ? [...current, item.id] : current.filter((x) => x !== item.id);

    if (checked && exclusive) {
      next = [item.id];
    } else if (checked) {
      next = next.filter((id) => !this.isExclusive(this.momentumProducts().find((x) => x.id === id)));
    }

    this.chosenProducts.set(next);
  }

  protected saveProducts(): void {
    this.store.saveEligibilityProducts(this.interventionId(), this.chosenProducts());
  }

  protected decide(): void {
    const value = this.decisionForm.getRawValue();
    this.store.decideEligibility(this.interventionId(), value.eligible === 'yes', nullIfBlank(value.detail));
  }

  private isExclusive(item: LookupResponse | undefined): boolean {
    return !!item?.metadata && item.metadata.includes('"exclusive":true');
  }
}
